import { useContext } from 'react';
import useFetch from 'hooks/api/useFetch';
import { API } from 'constants/global';
import { ToastContext, ToastContextProps } from 'contexts/ToastContext';
import { IHttpResponse } from 'helpers/api';
import { UserContext, UserContextProps } from 'contexts/UserContext';
import { getLocaleValue } from 'helpers/utils';
import { extractErrorMessage } from 'helpers/chatbotErrors';

export interface IChatbotThread {
  id: string;
  title: string;
  status: 'regular';
  createdAt?: Date;
  updatedAt?: Date;
}

// Thread dates, when the backend relays them from Dify: unix seconds, unix
// milliseconds or ISO strings. Absent from the current backend response.
const parseThreadDate = (value: unknown): Date | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const date =
    typeof value === 'number' ? new Date(value < 1e12 ? value * 1000 : value) : new Date(String(value));
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const normalizeThread = (raw: Record<string, unknown>): IChatbotThread => ({
  id: String(raw.id),
  title: String(raw.title ?? ''),
  status: raw.status as IChatbotThread['status'],
  createdAt: parseThreadDate(raw.createdAt ?? raw.created_at),
  updatedAt: parseThreadDate(raw.updatedAt ?? raw.updated_at),
});

export interface IChatbotMessage {
  id: string;
  role: 'user' | 'assistant';
  content: Array<{ type: string; text?: string }>;
  createdAt: string;
}

// Thread list, or `failed` with the backend / Dify error message when it could not be loaded
export interface IChatbotThreadsResult {
  threads?: IChatbotThread[];
  failed?: boolean;
  error?: string;
}

// SSE stream to read the answer from, `aborted` when cancelled by the user,
// or `failed` with the backend / Dify error message (coach unavailable)
export interface IChatbotSendResult {
  stream?: ReadableStream<Uint8Array>;
  aborted?: boolean;
  failed?: boolean;
  error?: string;
}

export interface IUseAPIChatbot {
  fetchThreads: () => Promise<IChatbotThreadsResult>;
  createThread: (localId: string) => Promise<{ id: string; external_id: string } | undefined>;
  sendMessage: (
    messages: Array<{ role: string; content: Array<{ type: string; text?: string }> }>,
    remoteId?: string,
    signal?: AbortSignal,
  ) => Promise<IChatbotSendResult>;
  fetchMessages: (conversationId: string) => Promise<IChatbotMessage[] | undefined>;
  archiveThread: (remoteId: string) => Promise<boolean>;
  unarchiveThread: (remoteId: string) => Promise<boolean>;
  deleteThread: (remoteId: string) => Promise<boolean>;
  renameThread: (remoteId: string, title: string) => Promise<boolean>;
  generateTitle: (remoteId: string, messages: Array<{ role: string; content: Array<{ type: string; text?: string }> }>) => Promise<string | undefined>;
  stopStreaming: (taskId: string) => Promise<boolean>;
}

function useAPIChatbot(): IUseAPIChatbot {
  const showLoadingLocal = () => {
    console.log('showLoadingLocal called');
  };
  const hideLoadingLocal = () => {
    console.log('hideLoadingLocal called');
  };
  const { setToast } = useContext(ToastContext) as ToastContextProps;
  const { user } = useContext(UserContext) as UserContextProps;
  const { getRequest, showLoading, hideLoading } = useFetch<any>();

  const fetchThreads = async (): Promise<IChatbotThreadsResult> => {
    try {
      const result: IHttpResponse = await getRequest({
        url: API.ROUTES.CHATBOT.THREADS,
        method: 'get',
        headers: {
          Authorization: `${user?.token?.type} ${user?.token?.value}`,
          'user-id': `${user?.id}`,
        },
      });

      const rawThreads = (result?.data?.threads as Array<Record<string, unknown>>) || [];
      return { threads: rawThreads.map(normalizeThread) };
    } catch (e) {
      // Backend or Dify down: the chat window shows the "coach unavailable"
      // message with the reason, no toast needed. HttpClient rejects with an
      // IHttpResponse whose `data` holds the server body and `message` the axios error.
      console.error('Error fetching chatbot threads:', e);
      const rejection = e as IHttpResponse;
      return {
        failed: true,
        error: extractErrorMessage(rejection?.data) || extractErrorMessage(rejection),
      };
    }
  };

  const createThread = async (
    localId: string,
  ): Promise<{ id: string; external_id: string } | undefined> => {
    try {
      const result: IHttpResponse = await getRequest({
        url: API.ROUTES.CHATBOT.THREADS,
        method: 'post',
        data: { localId },
        headers: {
          Authorization: `${user?.token?.type} ${user?.token?.value}`,
          'user-id': `${user?.id}`,
        },
      });

      if (result?.data?.id) {
        return {
          id: result.data.id,
          external_id: result.data.external_id,
        };
      }

      return undefined;
    } catch (e) {
      console.error('Error creating chatbot thread:', e);
      setToast({
        message: 'Failed to create conversation',
        status: 'danger',
      });
      return undefined;
    }
  };

  const sendMessage = async (
    messages: Array<{ role: string; content: Array<{ type: string; text?: string }> }>,
    remoteId?: string,
    signal?: AbortSignal,
  ): Promise<IChatbotSendResult> => {
    showLoadingLocal();

    try {
      const response = await fetch(API.ROUTES.CHATBOT.CHAT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `${user?.token?.type} ${user?.token?.value}`,
          'user-id': `${user?.id}`,
        },
        body: JSON.stringify({
          messages,
          metadata: {
            ... (remoteId ? { remoteId } : {} ),
            primary_color: getLocaleValue(user?.profileInfo?.colors?.primary?.name, 'fr') || undefined,
            secondary_color: getLocaleValue(user?.profileInfo?.colors?.secondary?.name, 'fr') || undefined,
            firstname: `${user?.firstname || ''}`.trim(),
          }
          
        }),
        signal, // Pass abort signal to fetch
      });

      hideLoadingLocal();

      if (!response.ok) {
        // The backend answered with an error (Dify unreachable, provider
        // failure...): relay its message so the chat can say why the coach is unavailable.
        const body = await response.text().catch(() => '');
        return {
          failed: true,
          error: extractErrorMessage(body) || response.statusText || `HTTP ${response.status}`,
        };
      }

      if (!response.body) {
        return { failed: true, error: response.statusText || `HTTP ${response.status}` };
      }

      return { stream: response.body };
    } catch (e) {
      hideLoadingLocal();
      if (e instanceof Error && e.name === 'AbortError') {
        return { aborted: true };
      }
      // Network failure: the chatbot backend itself is unreachable
      console.error('Error sending chatbot message:', e);
      return { failed: true, error: extractErrorMessage(e) };
    }
  };

  const fetchMessages = async (conversationId: string): Promise<IChatbotMessage[] | undefined> => {
    try {
      const result: IHttpResponse = await getRequest({
        url: `${API.ROUTES.CHATBOT.MESSAGES}?conversation_id=${conversationId}`,
        method: 'get',
        headers: {
          Authorization: `${user?.token?.type} ${user?.token?.value}`,
          'user-id': `${user?.id}`,
        },
      });

      if (result?.data?.messages) {
        return result.data.messages as IChatbotMessage[];
      }

      return [];
    } catch (e) {
      console.error('Error fetching messages:', e);
      return undefined;
    }
  };

  const archiveThread = async (remoteId: string): Promise<boolean> => {
    try {
      showLoading();
      const result: IHttpResponse = await getRequest({
        url: API.ROUTES.CHATBOT.THREAD_ARCHIVE(remoteId),
        method: 'post',
        headers: {
          Authorization: `${user?.token?.type} ${user?.token?.value}`,
          'user-id': `${user?.id}`,
        },
      });

      return result?.data?.success === true;
    } catch (e) {
      console.error('Error archiving thread:', e);
      setToast({
        message: 'Failed to archive conversation',
        status: 'danger',
      });
      return false;
    } finally {
      hideLoading();
    };
  };

  const unarchiveThread = async (remoteId: string): Promise<boolean> => {
    try {
      const result: IHttpResponse = await getRequest({
        url: API.ROUTES.CHATBOT.THREAD_UNARCHIVE(remoteId),
        method: 'post',
        headers: {
          Authorization: `${user?.token?.type} ${user?.token?.value}`,
          'user-id': `${user?.id}`,
        },
      });

      return result?.data?.success === true;
    } catch (e) {
      console.error('Error unarchiving thread:', e);
      setToast({
        message: 'Failed to unarchive conversation',
        status: 'danger',
      });
      return false;
    }
  };

  const deleteThread = async (remoteId: string): Promise<boolean> => {
    try {
      const result: IHttpResponse = await getRequest({
        url: API.ROUTES.CHATBOT.THREAD(remoteId),
        method: 'delete',
        headers: {
          Authorization: `${user?.token?.type} ${user?.token?.value}`,
          'user-id': `${user?.id}`,
        },
      });

      return result?.data?.success === true;
    } catch (e) {
      console.error('Error deleting thread:', e);
      setToast({
        message: 'Failed to delete conversation',
        status: 'danger',
      });
      return false;
    }
  };

  const renameThread = async (remoteId: string, title: string): Promise<boolean> => {
    try {
      const result: IHttpResponse = await getRequest({
        url: API.ROUTES.CHATBOT.THREAD(remoteId),
        method: 'patch',
        data: { title },
        headers: {
          Authorization: `${user?.token?.type} ${user?.token?.value}`,
          'Content-Type': 'application/json',
          'user-id': `${user?.id}`,
        },
      });

      return result?.data?.success === true;
    } catch (e) {
      console.error('Error renaming thread:', e);
      setToast({
        message: 'Failed to rename conversation',
        status: 'danger',
      });
      return false;
    }
  };

  const generateTitle = async (
    remoteId: string,
    messages: Array<{ role: string; content: Array<{ type: string; text?: string }> }>,
  ): Promise<string | undefined> => {
    try {
      const result: IHttpResponse = await getRequest({
        url: API.ROUTES.CHATBOT.THREAD_TITLE(remoteId),
        method: 'post',
        data: { messages },
        headers: {
          Authorization: `${user?.token?.type} ${user?.token?.value}`,
          'Content-Type': 'application/json',
          'user-id': `${user?.id}`,
        },
      });

      return result?.data?.title || undefined;
    } catch (e) {
      console.error('Error generating title:', e);
      return undefined;
    }
  };

  const stopStreaming = async (taskId: string): Promise<boolean> => {
    try {
      const result: IHttpResponse = await getRequest({
        url: API.ROUTES.CHATBOT.STOP_STREAMING(taskId),
        method: 'post',
        headers: {
          Authorization: `${user?.token?.type} ${user?.token?.value}`,
          'user-id': `${user?.id}`,
        },
      });
      return result?.data?.success === true || result?.status === 200;
    } catch (e) {
      console.error('Error stopping streaming:', e);
      return false;
    }
  };

  return {
    fetchThreads,
    createThread,
    sendMessage,
    fetchMessages,
    archiveThread,
    unarchiveThread,
    deleteThread,
    renameThread,
    generateTitle,
    stopStreaming,
  };
}

export default useAPIChatbot;
